/**
 * RealWorldActivityPulseCard — 24h × 6-signal activity heatmap.
 * 2026-07-03
 *
 * The founder's "when do humans actually show up" tile. Each row is
 * one platform signal (emails, ideas, invites, collectibles, chats,
 * logins) plotted across the 24 UTC hours of the day. Cell shade
 * intensity = event count in that hour bucket over the last 24h.
 *
 * Rendered in raw SVG so we avoid pulling in a chart library for
 * something this simple. Auto-refresh every 60s.
 *
 * Data source: GET /api/admin/activity/pulse
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Send, Lightbulb, UserPlus, Award, MessageSquare, LogIn,
  Activity, RefreshCw, Loader2, AlertTriangle, Sparkles, Info, X,
  ExternalLink,
} from 'lucide-react';
import { apiClient } from '../../api/client';

const ICON_MAP = {
  send: Send,
  lightbulb: Lightbulb,
  'user-plus': UserPlus,
  award: Award,
  'message-square': MessageSquare,
  'log-in': LogIn,
};

// Anomaly-badge styling. `kind` comes from the backend sentiment map.
const KIND_STYLES = {
  excellent: {
    ring: 'ring-emerald-500',
    dot: 'bg-emerald-500',
    label: 'EXCELLENT',
    icon: Sparkles,
    text: 'text-emerald-800',
    bg: 'bg-emerald-50 border-emerald-300',
  },
  warning: {
    ring: 'ring-rose-500',
    dot: 'bg-rose-500',
    label: 'ABNORMAL',
    icon: AlertTriangle,
    text: 'text-rose-800',
    bg: 'bg-rose-50 border-rose-300',
  },
  neutral: {
    ring: 'ring-amber-500',
    dot: 'bg-amber-500',
    label: 'NOTABLE',
    icon: Info,
    text: 'text-amber-800',
    bg: 'bg-amber-50 border-amber-300',
  },
};

const HOURS = Array.from({ length: 24 }, (_, i) => i);

/**
 * Convert (colour, count, maxCount) → a translucent fill so the eye
 * reads intensity without needing a legend. Empty cells stay a soft
 * grey so an "off" row still communicates "we watch this signal
 * but nothing happened".
 */
function cellFill(color, count, max) {
  if (!count) return '#E2E8F0'; // slate-200
  const t = Math.min(1, Math.max(0.15, count / Math.max(max, 1)));
  // Convert hex to rgb → rgba with alpha t
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${t.toFixed(2)})`;
}

const RealWorldActivityPulseCard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState(null);
  const [hovered, setHovered] = useState(null); // { signal, hour, count, annotation? }
  // Drill-down panel state
  const [drillCell, setDrillCell] = useState(null);   // { key, label, hour, color, count }
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillErr, setDrillErr] = useState(null);

  const fetchData = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await apiClient.request('/api/admin/activity/pulse');
      setData(res);
      setErr(null);
    } catch (e) {
      setErr(e?.message || 'Failed to load activity pulse');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Live current-hour poll — every 5s, only fetches the single hour we
  // care about (~60ms round trip vs. ~500ms for the full 24h grid) so
  // the tile feels alive during a demo without the refresh spinner
  // distracting. Silently patches into the existing grid via
  // `setData`; if the whole payload is stale we still fall through to
  // the 60s full refresh below.
  const fetchCurrentHour = useCallback(async () => {
    try {
      const cur = new Date().getUTCHours();
      const res = await apiClient.request(`/api/admin/activity/pulse?hour_only=${cur}`);
      if (!res?.signals) return;
      setData((prev) => {
        if (!prev?.signals) return prev;
        const bykey = Object.fromEntries(res.signals.map((s) => [s.key, s.count]));
        const nextSignals = prev.signals.map((s) => {
          if (!(s.key in bykey)) return s;
          const nextHourly = [...s.hourly];
          nextHourly[res.hour] = bykey[s.key];
          const total = nextHourly.reduce((a, b) => a + b, 0);
          const peak_count = Math.max(...nextHourly);
          const peak_hour = peak_count > 0 ? nextHourly.indexOf(peak_count) : null;
          return { ...s, hourly: nextHourly, total, peak_count, peak_hour };
        });
        const grand_total = nextSignals.reduce((a, s) => a + s.total, 0);
        return {
          ...prev,
          signals: nextSignals,
          grand_total,
          current_hour_utc: res.hour,
          live_updated_at: res.generated_at,
        };
      });
    } catch (_) {
      // Silent — the 60s full refresh will heal any drift.
    }
  }, []);

  useEffect(() => {
    fetchData();
    const full = setInterval(fetchData, 60_000);
    const live = setInterval(fetchCurrentHour, 5_000);
    return () => {
      clearInterval(full);
      clearInterval(live);
    };
  }, [fetchData, fetchCurrentHour]);

  // Compute per-signal max so intensity is relative WITHIN a row.
  // A row that peaks at 3 shouldn't disappear next to one that peaks
  // at 200 — otherwise ideas + invites become invisible next to logins.
  const rowMaxes = useMemo(() => {
    if (!data?.signals) return {};
    const m = {};
    for (const s of data.signals) m[s.key] = Math.max(1, ...s.hourly);
    return m;
  }, [data]);

  // Flatten annotations into a quick `${signal_key}::${hour}` lookup so
  // each cell can render its badge without scanning on every mouse move.
  const annotationIndex = useMemo(() => {
    const idx = {};
    if (!data?.signals) return idx;
    for (const s of data.signals) {
      for (const a of (s.annotations || [])) {
        idx[`${s.key}::${a.hour}`] = a;
      }
    }
    return idx;
  }, [data]);

  const openDrill = useCallback(async (signal) => {
    setDrillCell(signal);
    setDrillData(null);
    setDrillErr(null);
    setDrillLoading(true);
    try {
      const res = await apiClient.request(
        `/api/admin/activity/pulse/detail?signal=${encodeURIComponent(signal.key)}&hour=${signal.hour}&limit=15`
      );
      setDrillData(res);
    } catch (e) {
      setDrillErr(e?.message || 'Failed to load detail');
    } finally {
      setDrillLoading(false);
    }
  }, []);

  const closeDrill = useCallback(() => {
    setDrillCell(null);
    setDrillData(null);
    setDrillErr(null);
  }, []);

  return (
    <div
      data-testid="activity-pulse-card"
      className="rounded-2xl bg-white border-2 border-cyan-300 ring-1 ring-cyan-200/40 p-4 shadow-sm"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-cyan-700" strokeWidth={2} />
          <h3 className="text-sm font-bold text-slate-900 tracking-wide">
            Real-World Activity Pulse · 24h
          </h3>
        </div>
        <div className="flex items-center gap-3">
          {data && (
            <span
              data-testid="activity-pulse-grand-total"
              className="text-[11px] font-semibold text-slate-700 tabular-nums"
            >
              <span className="text-slate-900 font-black text-base">{data.grand_total}</span>
              <span className="ml-1 uppercase tracking-wider text-slate-600">events</span>
            </span>
          )}
          {data && (
            <span
              data-testid="activity-pulse-live-dot"
              className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-cyan-800"
              title="Current hour column refreshes every 5s"
            >
              <span className="relative inline-flex">
                <span className="absolute inline-flex h-2 w-2 rounded-full bg-cyan-400 opacity-75 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan-500" />
              </span>
              LIVE
            </span>
          )}
          <button
            data-testid="activity-pulse-refresh"
            onClick={fetchData}
            className="p-1 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition-colors"
            title="Refresh"
          >
            {refreshing ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.7} />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.7} />
            )}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-slate-600 py-6 text-center">Loading…</div>
      ) : err ? (
        <div className="text-xs text-rose-700 py-2" data-testid="activity-pulse-error">{err}</div>
      ) : (
        <>
          <div className="text-[11px] text-slate-700 mb-2">
            Each cell = events in one UTC hour of the last 24h. Intensity is relative to each
            row&apos;s own peak so quiet signals stay visible next to busy ones. Hover any cell for
            exact counts.
          </div>

          {/* Heatmap grid */}
          <div className="overflow-x-auto -mx-1 px-1">
            <div className="min-w-[640px]">
              {/* Column headers — hour labels every 3h to keep it tidy */}
              <div className="grid grid-cols-[148px_repeat(24,minmax(0,1fr))] gap-[3px] mb-1">
                <div />
                {HOURS.map((h) => (
                  <div
                    key={h}
                    className={`text-[9px] font-semibold tabular-nums text-center ${h === data.current_hour_utc ? 'text-cyan-800' : 'text-slate-600'}`}
                  >
                    {h % 3 === 0 ? `${String(h).padStart(2, '0')}` : ''}
                  </div>
                ))}
              </div>

              {/* Signal rows */}
              {data.signals.map((s) => {
                const Icon = ICON_MAP[s.icon] || Activity;
                const rowMax = rowMaxes[s.key] || 1;
                return (
                  <div
                    key={s.key}
                    data-testid={`activity-row-${s.key}`}
                    className="grid grid-cols-[148px_repeat(24,minmax(0,1fr))] gap-[3px] mb-[3px] items-center"
                  >
                    <div className="flex items-center gap-2 pr-2 min-w-0">
                      <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: s.color }} strokeWidth={2} />
                      <div className="min-w-0">
                        <div className="text-[11px] font-bold text-slate-900 truncate leading-tight">
                          {s.label}
                        </div>
                        <div className="text-[10px] text-slate-700 tabular-nums leading-tight">
                          {s.total} · {s.peak_hour != null ? (
                            <>peak {String(s.peak_hour).padStart(2, '0')}:00</>
                          ) : (
                            <span className="italic text-slate-500">quiet</span>
                          )}
                        </div>
                      </div>
                    </div>
                    {s.hourly.map((count, h) => {
                      const ann = annotationIndex[`${s.key}::${h}`];
                      const kindStyle = ann ? KIND_STYLES[ann.kind] || KIND_STYLES.neutral : null;
                      return (
                        <button
                          key={h}
                          type="button"
                          data-testid={`activity-cell-${s.key}-${h}`}
                          onMouseEnter={() => setHovered({
                            signal: s.label, hour: h, count, annotation: ann,
                          })}
                          onMouseLeave={() => setHovered(null)}
                          onClick={() => openDrill({
                            key: s.key, label: s.label, hour: h, color: s.color, count,
                          })}
                          style={{
                            background: cellFill(s.color, count, rowMax),
                            outline: h === data.current_hour_utc ? `1px solid ${s.color}` : 'none',
                          }}
                          className={`h-6 rounded-[3px] transition-transform hover:scale-125 hover:z-10 relative focus:outline-none focus:ring-2 focus:ring-cyan-500 cursor-pointer ${ann ? `ring-2 ${kindStyle.ring} ring-offset-1` : ''}`}
                          aria-label={`${s.label} at ${String(h).padStart(2, '0')}:00 UTC: ${count} events${ann ? ` — ${kindStyle.label}` : ''}`}
                        >
                          {ann && (
                            <span
                              className={`absolute -top-1 -right-1 w-2 h-2 rounded-full ${kindStyle.dot} ring-2 ring-white`}
                              aria-hidden
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Hover readout — extended to include the anomaly WHY when present */}
          <div className="mt-3 text-[11px]">
            <div className="flex items-center justify-between">
              <div className="text-slate-700" data-testid="activity-pulse-hover-readout">
                {hovered ? (
                  <>
                    <span className="font-bold text-slate-900">{hovered.signal}</span>
                    <span className="mx-1 text-slate-500">·</span>
                    <span className="tabular-nums font-semibold">
                      {String(hovered.hour).padStart(2, '0')}:00 UTC
                    </span>
                    <span className="mx-1 text-slate-500">·</span>
                    <span className="tabular-nums font-bold text-slate-900">
                      {hovered.count} event{hovered.count === 1 ? '' : 's'}
                    </span>
                    {hovered.annotation && (
                      <span
                        className={`ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest ${KIND_STYLES[hovered.annotation.kind].bg} ${KIND_STYLES[hovered.annotation.kind].text} border`}
                        data-testid="activity-pulse-hover-badge"
                      >
                        {React.createElement(KIND_STYLES[hovered.annotation.kind].icon, { className: 'w-3 h-3' })}
                        {KIND_STYLES[hovered.annotation.kind].label}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="italic text-slate-500">Hover any cell for exact counts. Click a cell to drill into the underlying events.</span>
                )}
              </div>
              <div className="text-[10px] text-slate-600 font-medium shrink-0 ml-2">
                Current hour: <span className="text-cyan-800 font-bold tabular-nums">{String(data.current_hour_utc).padStart(2, '0')}:00 UTC</span>
              </div>
            </div>
            {hovered?.annotation && (
              <div
                data-testid="activity-pulse-hover-why"
                className={`mt-1.5 rounded-md border px-2.5 py-1.5 ${KIND_STYLES[hovered.annotation.kind].bg}`}
              >
                <span className={`text-[11px] leading-snug ${KIND_STYLES[hovered.annotation.kind].text}`}>
                  {hovered.annotation.why}
                </span>
              </div>
            )}
          </div>

          {/* Drill-down panel — slides up from below the heatmap when a cell is clicked */}
          {drillCell && (
            <div
              data-testid="activity-pulse-drill-panel"
              className="mt-4 rounded-xl border-2 bg-white shadow-md overflow-hidden"
              style={{ borderColor: drillCell.color }}
            >
              <div className="flex items-center justify-between px-4 py-2.5 border-b" style={{ background: `${drillCell.color}15` }}>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: drillCell.color }} />
                  <span className="text-sm font-bold text-slate-900">{drillCell.label}</span>
                  <span className="text-slate-500">·</span>
                  <span className="text-xs font-semibold text-slate-700 tabular-nums">
                    {String(drillCell.hour).padStart(2, '0')}:00 UTC
                  </span>
                  <span className="text-slate-500">·</span>
                  <span className="text-xs font-bold text-slate-900 tabular-nums">
                    {drillCell.count} event{drillCell.count === 1 ? '' : 's'}
                  </span>
                </div>
                <button
                  data-testid="activity-pulse-drill-close"
                  onClick={closeDrill}
                  className="p-1 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition-colors"
                  aria-label="Close detail panel"
                >
                  <X className="w-4 h-4" strokeWidth={2} />
                </button>
              </div>
              <div className="px-4 py-3">
                {drillLoading ? (
                  <div className="text-xs text-slate-600 py-3 text-center">
                    <Loader2 className="w-4 h-4 animate-spin inline-block mr-1.5" />
                    Fetching underlying events…
                  </div>
                ) : drillErr ? (
                  <div className="text-xs text-rose-700 py-1" data-testid="activity-pulse-drill-error">{drillErr}</div>
                ) : drillData && drillData.rows.length === 0 ? (
                  <div className="text-xs text-slate-600 italic py-1">
                    No events found in this hour. If the cell shows a count above zero,
                    the underlying rows may live in a collection this drill-down
                    doesn&apos;t yet index.
                  </div>
                ) : drillData && (
                  <>
                    <div className="text-[10px] uppercase tracking-widest text-slate-600 font-bold mb-2">
                      Sample events · showing {drillData.count} of {drillCell.count}
                    </div>
                    <ul className="space-y-1.5 max-h-64 overflow-y-auto pr-1" data-testid="activity-pulse-drill-rows">
                      {drillData.rows.map((row, i) => {
                        // Extract user identity so we can render a
                        // "jump to user" chip separately from the raw
                        // KV dump — closes the loop from spike → cell
                        // → user in one click.
                        const userIdentifier = row.user_id || row.user_email || row.owner_email
                          || row.inviter_email || row.invitee_email || row.to;
                        return (
                          <li
                            key={i}
                            className="text-[11px] px-2.5 py-1.5 rounded bg-slate-50 border border-slate-200 flex items-start gap-2"
                          >
                            <span className="tabular-nums text-slate-500 shrink-0 mt-0.5">{i + 1}.</span>
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                                {Object.entries(row).map(([k, v]) => (
                                  <span key={k}>
                                    <span className="text-slate-500 font-semibold uppercase text-[9px] tracking-wider">{k}:</span>{' '}
                                    <span className="font-mono text-slate-800 break-all">
                                      {typeof v === 'string' && v.length > 60 ? v.slice(0, 60) + '…' : String(v)}
                                    </span>
                                  </span>
                                ))}
                              </div>
                              {userIdentifier && (
                                <button
                                  type="button"
                                  data-testid={`activity-drill-jump-user-${i}`}
                                  onClick={() => {
                                    closeDrill();
                                    navigate(`/admin/users?search=${encodeURIComponent(userIdentifier)}`);
                                  }}
                                  className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold text-cyan-800 hover:text-cyan-900 hover:underline transition-colors"
                                  title={`Open in Users → ${userIdentifier}`}
                                >
                                  <ExternalLink className="w-3 h-3" strokeWidth={2} />
                                  Open in Users → {userIdentifier.length > 32 ? userIdentifier.slice(0, 32) + '…' : userIdentifier}
                                </button>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RealWorldActivityPulseCard;
